import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import deepEqual from 'fast-deep-equal/es6/react';
import { FileUploadModal as FileUpload, ALLOWED_FILE_TYPES } from 'shared-components';
import { ApplicationState } from '../../../../store';
import { closeModal } from '../../../../store/ui/actions';
import {
  saveDocuments,
  saveFinanceDocuments,
  saveFollowUpDocuments,
  saveInterviewDocuments,
  saveRecordedInterviewDocuments,
} from '../../../../store/student/actions';
import { IModal } from '../../../../store/ui/types';
import { deleteFile, getUploadUrl, getUploadUrlFailure } from '../../../../store/data/actions';
import { hasPerm, P } from '../../../../utilities/roles/permissions';

export const FileUploadModal = (modalProps: IModal) => {
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const dispatch = useDispatch();
  const userRole = useSelector(({ auth }: ApplicationState) => auth.role, deepEqual);
  const uploadFile = useSelector(({ data }: ApplicationState) => data.uploadFile, deepEqual);

  const canDelete = hasPerm(userRole, P.CAN_DELETE_FILE);

  const onDelete = () =>
    canDelete
      ? (
          fileName: string,
          sponsorType: string | null,
          sponsorKey: string | null,
          docType: string | null,
          docKey: string | null,
          interviewType: string,
          eventType: string,
          eventKey: string,
          interviewHash: string,
          attemptHash: string,
          isFinance: boolean,
          isFollowUp: boolean,
          isInterview: boolean,
          isRecordedInterview: boolean,
          deletedBy: string | undefined,
        ) => {
          dispatch(
            deleteFile(
              fileName,
              sponsorType,
              sponsorKey,
              docType,
              docKey,
              interviewType,
              eventType,
              eventKey,
              interviewHash,
              attemptHash,
              isFinance,
              isFollowUp,
              isInterview,
              isRecordedInterview,
              deletedBy,
            ),
          );
        }
      : undefined;

  const onContinue =
    () =>
    (
      sponsorType: string,
      sponsorKey: string | null,
      docType: string | null,
      docKey: string | null,
      interviewType: string,
      eventType: string,
      eventKey: string,
      interviewHash: string,
      attemptHash: string,
      isFinance: boolean,
      isFollowUp: boolean,
      isInterview: boolean,
      isRecordedInterview: boolean,
      uploadedBy: string | undefined,
    ) => {
      if (isRecordedInterview) {
        dispatch(saveRecordedInterviewDocuments(interviewHash, attemptHash));
        dispatch(closeModal());
      } else if (isInterview) {
        dispatch(saveInterviewDocuments(interviewType, eventType, eventKey));
        dispatch(closeModal());
      } else if (isFinance) {
        dispatch(saveFinanceDocuments(sponsorType, sponsorKey, docType, docKey, uploadedBy ?? ''));
        dispatch(closeModal());
      } else if (isFollowUp) {
        dispatch(saveFollowUpDocuments(sponsorType, sponsorKey, docType!, docKey!, uploadedBy ?? ''));
        dispatch(closeModal());
      } else {
        dispatch(saveDocuments(sponsorType, sponsorKey!, docType, docKey, uploadedBy ?? ''));
        dispatch(closeModal());
      }
    };
  const isValidFile = (fileName: string) => {
    const extension: string = fileName?.split('.').pop() ?? '';
    return ALLOWED_FILE_TYPES.includes(extension);
  };
  const onUpload = (openModal: IModal, fileName: string, fileType: string, fileIndex: number, file: File) => {
    if (!isValidFile(fileName)) {
      setIsUploadFailed(true);
      return dispatch(
        getUploadUrlFailure(
          `Please upload the following file types: ${ALLOWED_FILE_TYPES.map(s => `.${s}`).join(', ')}`,
          {
            name: fileName,
            index: fileIndex,
          },
        ),
      );
    }
    setIsUploadFailed(false);
    return dispatch(getUploadUrl(openModal, fileName, fileType, fileIndex, file)); // 2. step
  };
  return (
    <FileUpload
      userRole={userRole}
      uploadFileName={uploadFile?.name}
      uploadFileStatus={uploadFile?.status}
      uploadFileIndex={uploadFile?.index}
      isFileUploading={uploadFile?.isFileUploading}
      isUploadFailed={isUploadFailed}
      openModal={modalProps}
      onClose={() => dispatch(closeModal())}
      onUploadFile={(openModal: IModal, fileName: string, fileType: string, fileIndex: number, file: File) => {
        onUpload(openModal, fileName, fileType, fileIndex, file);
      }}
      onDeleteFile={onDelete()}
      onContinue={onContinue()}
    />
  );
};
